import {
  getAccessTokenFromStorage,
  getRefreshTokenFromStorage,
  removeToken,
  setAccessToken,
  setRefreshToken,
} from '@utils/token/authTokens';
import {
  ACCESS_TOKEN_KEY,
  IMPERSONATE_ACCESS_TOKEN_KEY,
  IMPERSONATE_REFRESH_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  STORAGE_ACCESS_TOKEN_KEY,
  STORAGE_IMPERSONATE_ACCESS_TOKEN_KEY,
  STORAGE_IMPERSONATE_REFRESH_TOKEN_KEY,
  STORAGE_REFRESH_TOKEN_KEY,
} from '@utils/token/constants';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';

export const useTokenManagement = () => {
  const getTokens = ({ isImpersonate = false }: { isImpersonate?: boolean } = {}) => {
    const accessCookieKey = isImpersonate ? IMPERSONATE_ACCESS_TOKEN_KEY : ACCESS_TOKEN_KEY;
    const refreshCookieKey = isImpersonate ? IMPERSONATE_REFRESH_TOKEN_KEY : REFRESH_TOKEN_KEY;
    const accessStorageKey = isImpersonate ? STORAGE_IMPERSONATE_ACCESS_TOKEN_KEY : STORAGE_ACCESS_TOKEN_KEY;
    const refreshStorageKey = isImpersonate ? STORAGE_IMPERSONATE_REFRESH_TOKEN_KEY : STORAGE_REFRESH_TOKEN_KEY;

    let accessToken = Cookies.get(accessCookieKey as string);
    let refreshToken = Cookies.get(refreshCookieKey as string);

    // Fallback: if a cookie is missing, try reading from browser storage.
    // When found, restore the cookie so subsequent operations (e.g. request() Authorization header) work.
    if (!accessToken) {
      const storedAccess = getAccessTokenFromStorage(accessStorageKey as string);
      if (storedAccess) {
        setAccessToken({ key: accessCookieKey as string, token: storedAccess });
        accessToken = storedAccess;
      }
    }

    if (!refreshToken) {
      const storedRefresh = getRefreshTokenFromStorage(refreshStorageKey as string);
      if (storedRefresh) {
        setRefreshToken({ key: refreshCookieKey as string, token: storedRefresh });
        refreshToken = storedRefresh;
      }
    }

    return { accessToken, refreshToken };
  };

  const setTokens = ({
    accessToken,
    refreshToken,
    isImpersonate,
  }: {
    accessToken?: string;
    refreshToken: string;
    isImpersonate?: boolean;
  }) => {
    if (isImpersonate) {
      accessToken && setAccessToken({ key: IMPERSONATE_ACCESS_TOKEN_KEY, token: accessToken });
      setRefreshToken({ key: IMPERSONATE_REFRESH_TOKEN_KEY, token: refreshToken });
    } else {
      accessToken && setAccessToken({ key: ACCESS_TOKEN_KEY, token: accessToken });
      setRefreshToken({ key: REFRESH_TOKEN_KEY, token: refreshToken });
    }
  };

  const clearTokens = ({ isImpersonate = false }: { isImpersonate?: boolean } = {}) => {
    if (!isImpersonate) {
      removeToken(REFRESH_TOKEN_KEY as string);
      removeToken(ACCESS_TOKEN_KEY as string);
    }
    removeToken(IMPERSONATE_ACCESS_TOKEN_KEY as string);
    removeToken(IMPERSONATE_REFRESH_TOKEN_KEY as string);
  };

  const getIsAdmin = ({ isImpersonate = false }: { isImpersonate?: boolean } = {}) => {
    const accessToken = getTokens({ isImpersonate }).accessToken;
    const decodedToken: { isAdmin: boolean } = accessToken ? jwtDecode(accessToken) : { isAdmin: false };

    return decodedToken.isAdmin;
  };

  return {
    getIsAdmin,
    getTokens,
    setTokens,
    clearTokens,
  };
};
